import { GolferShortName, MITee } from '@app/evaluation-core/types'
import React, { useEffect, useRef } from 'react'
import {
  StickyContainer,
  TeeRowScorer,
  TeeRowScorerItem,
  TeeRowTee,
  TeeRowTeeLabel,
  TeeRowTeeLabelText,
  TeeRowTeeName,
  TeeRowTeeNameLabelText,
} from './TeeItem.styles'
import { getAdjustmentValue, resetTee } from '@app/evaluation-core'
import { resetModifications } from '@app/evaluation-core/modifications'
import { setCollapsedTees } from '@app/evaluation-core/actions/evaluationActions'

import useAdjustment from '@common/hooks/useAdjustment'
import { useDispatch } from 'react-redux'
import useEvaluation from '@common/hooks/useEvaluation'
import useEvaluationUser from '@common/hooks/useEvaluationUser'
import useUser from '@common/hooks/useUser'
import TeeOptionsModal from './TeeOptionsModal'

interface TeeItemProps {
  tee: MITee
  index: number
  modalOpen?: boolean
}

export const TeeItemHeader = ({ tee, index, modalOpen }: TeeItemProps) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const { currentId, evaluation } = useEvaluation()
  const { view } = useEvaluationUser()
  const isPhone = window.innerWidth <= 767

  const adjustments = useAdjustment()
  const userId = useUser().id.toString()
  const dispatch = useDispatch<any>()

  const handleResetTee = (teeIndex: number) => {
    if (!currentId) return
    resetModifications({
      evaluationId: currentId,
      hole: view?.hole,
      tee: teeIndex,
    })
    resetTee(view?.hole, teeIndex, currentId)
  }

  const handleResetTeeOnAllHoles = (teeIndex: number) => {
    if (!currentId) return
    evaluation?.holes.forEach((hole, index) => {
      resetModifications({
        evaluationId: currentId,
        hole: hole.holeIndex,
        tee: teeIndex,
      })
      resetTee(index, teeIndex, currentId)
    })
  }

  useEffect(() => {
    if (tee.teeIndex === view.tee && ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      })
    }
  }, [view])

  if (!adjustments.adjustments) {
    return null
  }

  const allTeeIndexes = evaluation?.holes[view?.hole].tees.map(
    (tee) => tee?.teeIndex
  )

  const _collapsedTees: number[] = view?.collapsedTees || []
  const collapsed = _collapsedTees.includes(tee.teeIndex)

  const openAllTees = () => dispatch(setCollapsedTees([], userId))

  const closeAllTees = () =>
    dispatch(setCollapsedTees(allTeeIndexes as number[], userId))

  const collapseTee = (e) => {
    e.stopPropagation()
    e.preventDefault()

    if (!collapsed) {
      const tees = [..._collapsedTees, tee.teeIndex]
      if (e.shiftKey) {
        return closeAllTees()
      }
      return dispatch(setCollapsedTees(tees, userId))
    }
    const filteredTees = _collapsedTees.filter((collapsedTee) => {
      return +collapsedTee !== +tee.teeIndex
    })
    if (e.shiftKey) {
      return openAllTees()
    }
    return dispatch(setCollapsedTees(filteredTees, userId))
  }

  const teeLength =
    adjustments &&
    getAdjustmentValue('hole', view, `tee${tee.teeNumber}Length`, adjustments)

  const getTeeLength = (teeNumber: number) =>
    adjustments
      ? (getAdjustmentValue(
          'hole',
          view,
          `tee${teeNumber}Length`,
          adjustments
        ) as number)
      : 0

  const teeNumberText = `Tee: ${tee.teeNumber}`

  const currentTeeIndex = evaluation?.holes[view?.hole]?.tees[view.tee].teeIndex

  const tees = evaluation?.holes[view?.hole].tees.map((tee) => tee) as MITee[]

  const teeNameText = `(${tee.name})`

  return (
    <StickyContainer>
      <TeeRowTee selected={tee.teeIndex === currentTeeIndex}>
        {!collapsed && (
          <>
            <TeeRowTeeLabelText
              style={{
                maxWidth: !isPhone ? 50 : 25,
                fontSize: !isPhone ? 16 : 9,
              }}
            >
              {teeLength}
            </TeeRowTeeLabelText>
            <TeeRowTeeLabel>
              <TeeRowTeeLabelText>{teeNumberText}</TeeRowTeeLabelText>
            </TeeRowTeeLabel>
          </>
        )}
        {!modalOpen && (
          <TeeOptionsModal
            collapsed={collapsed}
            evaluation={evaluation}
            view={view}
            tees={tees}
            tee={tee}
            getTeeLength={getTeeLength}
            collapseTee={collapseTee}
            handleResetTee={handleResetTee}
            handleResetTeeOnAllHoles={handleResetTeeOnAllHoles}
            index={index}
          />
        )}
      </TeeRowTee>
      <TeeRowTeeName>
        <TeeRowTeeLabel>
          <TeeRowTeeNameLabelText>{teeNameText}</TeeRowTeeNameLabelText>
        </TeeRowTeeLabel>
      </TeeRowTeeName>
      <TeeRowScorer>
        {tee.golfers
          .map((golferShortName: GolferShortName, typeIndex: number) => (
            <div key={typeIndex}>
              <TeeRowScorerItem
                selected={
                  tee.teeIndex === currentTeeIndex &&
                  view.golfer === golferShortName
                }
              >
                {golferShortName}
              </TeeRowScorerItem>
            </div>
          ))
          .slice(0, collapsed ? 1 : tee.golfers.length)}
      </TeeRowScorer>
    </StickyContainer>
  )
}
